'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  signupRewardVerificationClaim,
  signupRewardVerificationCreateCheckout,
  signupRewardVerificationGetState,
} from '@/app/apis';
import { getRequestPlatform, SITE } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { SignupReward, SignupRewardType, SignupRewardVerificationState } from '@/app/types';
import { useCredits } from './use-credits';
import { useFetch } from './use-fetch';

const POLL_INTERVAL = 3000;
const MAX_POLLS = 20;
type VerificationError =
  | 'loadError'
  | 'checkoutError'
  | 'claimError'
  | 'invalidCheckout'
  | 'cardAlreadyUsed'
  | 'replaceCardCheckoutError';
type RewardErrors = Partial<Record<SignupRewardType, VerificationError>>;
type RewardAction = { userId: string; rewardType: SignupRewardType; kind: 'checkout' | 'claim' };
type ClaimOutcome = { claimResult: 'claimed' | 'requires_verification'; reward: SignupReward; claimedCredits: number };

const getStripeCheckoutUrl = (checkoutUrl: string): string | null => {
  try {
    const url = new URL(checkoutUrl);
    return url.origin === 'https://checkout.stripe.com' && !url.username && !url.password ? url.href : null;
  } catch {
    return null;
  }
};

export const useSignupRewardVerification = ({
  userId: requestedUserId,
  rewardType,
  sessionId,
}: {
  userId?: string;
  rewardType?: SignupRewardType;
  sessionId?: string;
}) => {
  const webAvailable = SITE === 'global' && getRequestPlatform() === 'web';
  const userId = webAvailable ? requestedUserId : undefined;
  const openModal = useStore((store) => store.openModal);
  const { updateCreditsAsync } = useCredits();
  const key = `${userId ?? ''}:${rewardType ?? ''}:${sessionId ?? ''}`;
  const currentKey = useRef(key);
  currentKey.current = key;
  const [result, setResult] = useState<{
    key: string;
    state?: SignupRewardVerificationState;
    unavailable?: boolean;
  }>();
  const latestResult = useRef(result);
  const mutationVersion = useRef(0);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ key: string; load?: VerificationError; rewards: RewardErrors }>();
  const [activeAction, setActiveAction] = useState<RewardAction | null>(null);
  const actionRef = useRef<RewardAction | null>(null);
  const pollCount = useRef(0);
  const [pollingStopped, setPollingStopped] = useState(false);
  const state = result?.key === key && userId ? result.state : undefined;
  const reward = state?.rewards?.find((item) => item.reward_type === rewardType);
  const unavailable = !webAvailable || (result?.key === key && result.unavailable);
  const rewardErrors = errors?.key === key ? errors.rewards : {};
  const error = errors?.key === key ? errors.load || (rewardType && errors.rewards[rewardType]) : undefined;
  const action = activeAction && activeAction.userId === userId ? activeAction.kind : null;
  const actionRewardType = activeAction && activeAction.userId === userId ? activeAction.rewardType : undefined;
  const needsNewCard = error === 'cardAlreadyUsed' || error === 'replaceCardCheckoutError';

  // Suppress shared toasts; the account/reward/session-scoped callers below handle failures.
  const { runAsync: getStateAsync } = useFetch(signupRewardVerificationGetState, {
    manual: true,
    onError: () => {},
  });
  const { runAsync: createCheckoutAsync } = useFetch(signupRewardVerificationCreateCheckout, {
    manual: true,
    onError: () => {},
  });
  const { runAsync: claimAsync } = useFetch(signupRewardVerificationClaim, {
    manual: true,
    onError: () => {},
  });

  const loadState = useCallback(
    async (signal?: AbortSignal) => {
      if (!userId) return;
      const version = mutationVersion.current;
      setLoading(true);
      try {
        const reply = await getStateAsync({
          query: rewardType || sessionId ? { reward_type: rewardType, session_id: sessionId } : undefined,
          signal,
        });
        if (signal?.aborted || currentKey.current !== key || mutationVersion.current !== version) return;
        if (reply.response?.status === 404) {
          latestResult.current = { key, unavailable: true };
          setResult(latestResult.current);
          setErrors(undefined);
        } else if (!reply.error && reply.data && Array.isArray(reply.data.rewards)) {
          const previous = latestResult.current?.key === key ? latestResult.current.state : undefined;
          const newlyClaimed =
            !rewardType &&
            !sessionId &&
            reply.data.enabled &&
            previous?.enabled &&
            previous?.rewards.some(
              (item) =>
                ['pending_claim', 'pending_verification', 'ready_to_claim'].includes(item.reward_status) &&
                reply.data?.rewards.some(
                  (next) => next.reward_type === item.reward_type && next.reward_status === 'claimed',
                ),
            );
          latestResult.current = { key, state: reply.data };
          setResult(latestResult.current);
          // The original page can outlive its Checkout dialog. Reuse the payment
          // balance updater when another tab completes a previously pending reward.
          if (newlyClaimed) void updateCreditsAsync().catch(() => {});
          setErrors((previous) => ({
            key,
            rewards: Object.fromEntries(
              Object.entries(previous?.key === key ? previous.rewards : {}).filter(
                ([type, previousError]) =>
                  ['claimError', 'cardAlreadyUsed', 'replaceCardCheckoutError'].includes(previousError ?? '') &&
                  reply.data?.rewards?.some(
                    (item) => item.reward_type === type && !['claimed', 'unavailable'].includes(item.reward_status),
                  ),
              ),
            ),
          }));
        } else {
          setErrors((previous) => ({ key, load: 'loadError', rewards: previous?.key === key ? previous.rewards : {} }));
        }
      } catch {
        if (!signal?.aborted && currentKey.current === key && mutationVersion.current === version) {
          setErrors((previous) => ({ key, load: 'loadError', rewards: previous?.key === key ? previous.rewards : {} }));
        }
      } finally {
        if (!signal?.aborted && currentKey.current === key) setLoading(false);
      }
    },
    [key, userId, rewardType, sessionId, getStateAsync, updateCreditsAsync],
  );

  useEffect(() => {
    const controller = new AbortController();
    pollCount.current = 0;
    setPollingStopped(false);
    setErrors(undefined);
    if (userId) void loadState(controller.signal);
    return () => controller.abort();
  }, [userId, loadState]);

  useEffect(() => {
    const pending = rewardType
      ? reward?.verification_status === 'pending'
      : state?.rewards?.some((item) => item.verification_status === 'pending');
    if (!state?.enabled || !pending) return;
    if (pollCount.current >= MAX_POLLS) {
      setPollingStopped(true);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      pollCount.current += 1;
      void loadState(controller.signal);
    }, POLL_INTERVAL);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [result, state, rewardType, reward, loadState]);

  const refresh = useCallback(() => {
    pollCount.current = 0;
    setPollingStopped(false);
    return loadState();
  }, [loadState]);

  useEffect(() => {
    if (!userId || !state?.enabled) return;
    const onFocus = () => void refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [userId, state?.enabled, refresh]);

  const setRewardError = (type: SignupRewardType, nextError?: VerificationError) => {
    if (currentKey.current !== key) return;
    setErrors((previous) => ({
      key,
      rewards: { ...(previous?.key === key ? previous.rewards : {}), [type]: nextError },
    }));
  };

  const beginAction = (type: SignupRewardType, kind: RewardAction['kind']): RewardAction | null => {
    if (!userId || actionRef.current?.userId === userId) return null;
    const next = { userId, rewardType: type, kind };
    actionRef.current = next;
    setActiveAction(next);
    setRewardError(type);
    return next;
  };

  const finishAction = (requestAction: RewardAction) => {
    if (actionRef.current !== requestAction) return;
    actionRef.current = null;
    setActiveAction(null);
  };

  const createCheckout = async (type: SignupRewardType, returnUrl: string): Promise<string | null> => {
    const current = latestResult.current?.key === key ? latestResult.current.state : undefined;
    const selected = current?.rewards?.find((item) => item.reward_type === type);
    if (
      !userId ||
      !current?.enabled ||
      !selected ||
      !['pending_verification', 'ready_to_claim'].includes(selected.reward_status)
    )
      return null;
    const previousError = rewardErrors[type];
    const checkoutError =
      previousError === 'cardAlreadyUsed' || previousError === 'replaceCardCheckoutError'
        ? 'replaceCardCheckoutError'
        : 'checkoutError';
    const requestAction = beginAction(type, 'checkout');
    if (!requestAction) return null;
    try {
      const reply = await createCheckoutAsync({ body: { reward_type: type, return_url: returnUrl } });
      if (currentKey.current !== key) return null;
      if (reply.error || !reply.data?.session_id) {
        setRewardError(type, checkoutError);
        return null;
      }
      mutationVersion.current += 1;
      const checkoutUrl = getStripeCheckoutUrl(reply.data.checkout_url);
      if (!checkoutUrl) {
        setRewardError(type, checkoutError === 'replaceCardCheckoutError' ? checkoutError : 'invalidCheckout');
        return null;
      }
      openModal('signupRewardCheckout', { userId, rewardType: type, sessionId: reply.data.session_id });
      return checkoutUrl;
    } catch {
      setRewardError(type, checkoutError);
      return null;
    } finally {
      finishAction(requestAction);
    }
  };

  const claimReward = async (type: SignupRewardType, verifiedSessionId?: string): Promise<ClaimOutcome | null> => {
    const current = latestResult.current?.key === key ? latestResult.current.state : undefined;
    const selected = current?.rewards?.find((item) => item.reward_type === type);
    if (!current?.enabled || !selected || ['claimed', 'unavailable'].includes(selected.reward_status)) return null;
    if (
      selected.verification_status === 'succeeded' &&
      selected.reward_status === 'ready_to_claim' &&
      !verifiedSessionId
    ) {
      setRewardError(type, 'loadError');
      return null;
    }
    if (
      verifiedSessionId &&
      (selected.session_id !== verifiedSessionId || selected.verification_status !== 'succeeded')
    ) {
      setRewardError(type, 'loadError');
      return null;
    }
    const requestAction = beginAction(type, 'claim');
    if (!requestAction) return null;
    try {
      const reply = await claimAsync({
        body: { reward_type: type, ...(verifiedSessionId ? { session_id: verifiedSessionId } : {}) },
      });
      if (currentKey.current !== key) return null;
      const claimedReward = Array.isArray(reply.data?.rewards)
        ? reply.data.rewards.find((item) => item.reward_type === type)
        : undefined;
      if (
        !reply.error &&
        reply.data?.reward_type === type &&
        claimedReward &&
        (reply.data.claim_result === 'claimed' || reply.data.claim_result === 'requires_verification')
      ) {
        mutationVersion.current += 1;
        latestResult.current = { key, state: reply.data };
        setResult(latestResult.current);
        return {
          claimResult: reply.data.claim_result,
          reward: claimedReward,
          claimedCredits: reply.data.claimed_credits ?? 0,
        };
      }
      setRewardError(
        type,
        reply.error?.reason === 'SIGNUP_REWARD_CARD_ALREADY_USED' ? 'cardAlreadyUsed' : 'claimError',
      );
      return null;
    } catch {
      setRewardError(type, 'claimError');
      return null;
    } finally {
      finishAction(requestAction);
    }
  };

  return {
    state,
    reward,
    unavailable,
    loading,
    error,
    rewardErrors,
    action,
    actionRewardType,
    needsNewCard,
    pollingStopped,
    refresh,
    createCheckout,
    claimReward,
  };
};

// Share the claim/verification decision while callers retain account ownership and UI updates.
export const claimSignupReward = async (
  verification: Pick<
    ReturnType<typeof useSignupRewardVerification>,
    'state' | 'rewardErrors' | 'claimReward' | 'createCheckout'
  >,
  type: SignupRewardType,
  returnUrl: string,
  isCurrent: () => boolean,
) => {
  const selected = verification.state?.rewards.find((item) => item.reward_type === type);
  const error = verification.rewardErrors[type];
  const replaceCard = error === 'cardAlreadyUsed' || error === 'replaceCardCheckoutError';
  const sessionId =
    selected?.verification_status === 'succeeded' && selected.reward_status === 'ready_to_claim'
      ? selected.session_id
      : undefined;
  const outcome = replaceCard ? null : await verification.claimReward(type, sessionId);
  if (!isCurrent() || (!replaceCard && !outcome)) return null;
  if (outcome?.claimResult === 'claimed') return { outcome, checkoutUrl: null };
  const checkoutUrl = await verification.createCheckout(type, returnUrl);
  return isCurrent() ? { outcome, checkoutUrl } : null;
};
